import { useContext, useMemo } from "react";
import AccessService from "@me-team/host/services/AccessService";
import { AccessContext } from "@me-team/host/src/components/App/App";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { useModulesAccess } from "@me-team/host/src/utils/useModulesAccess";
import { AllModulesTagName } from "../../../utils/enums";
import { CompanyAccess, PermissionScope } from "@me-team/host/main/graphql/types";
import { EmployeesListAccess } from "../types/employeesListAccess";

interface UseEmployeesListAccessParams {
    newEmployeeId?: number | string | null;
}

export const useEmployeesListAccess = ({ newEmployeeId }: UseEmployeesListAccessParams = {}): EmployeesListAccess => {
    const accessService = AccessService.getInstance();
    const { isReady: isAccessContextReady } = useContext(AccessContext);

    const canImportEmployees = useRoleAccess(CompanyAccess.EmployeeImport);
    const canExportEmployees = useRoleAccess(CompanyAccess.EmployeeExport);
    const canCreateEmployee = useRoleAccess(CompanyAccess.EmployeeCreate);
    const canAddEmployeeFromAnotherBranch = useRoleAccess(CompanyAccess.AccessManageBranchEmployees);

    const canViewEmployeeListAll = useRoleAccess(CompanyAccess.EmployeeListView, { scope: PermissionScope.All });
    const canViewEmployeeListSelf = useRoleAccess(CompanyAccess.EmployeeListView, { scope: PermissionScope.Self });

    const canViewAccessSwitches = useRoleAccess(CompanyAccess.AccessSettingsView);
    const canManageAccessSwitches = useRoleAccess(CompanyAccess.AccessManageBranchEmployees);

    const canViewEmployeeEntryAll = useRoleAccess(CompanyAccess.EmployeeView, { scope: PermissionScope.All });
    const canViewEmployeeEntrySelf = useRoleAccess(CompanyAccess.EmployeeView, { scope: PermissionScope.Self });
    const canEditEmployeeEntryAll = useRoleAccess(CompanyAccess.EmployeeEdit, { scope: PermissionScope.All });
    const canEditEmployeeEntrySelf = useRoleAccess(CompanyAccess.EmployeeEdit, { scope: PermissionScope.Self });

    const canViewSalaryAll = useRoleAccess(CompanyAccess.SalarySettingsView, { scope: PermissionScope.All });
    const canViewSalarySelf = useRoleAccess(CompanyAccess.SalarySettingsView, { scope: PermissionScope.Self });
    const canEditSalaryAll = useRoleAccess(CompanyAccess.SalarySettingsEdit, { scope: PermissionScope.All });
    const canEditSalarySelf = useRoleAccess(CompanyAccess.SalarySettingsEdit, { scope: PermissionScope.Self });

    const canViewReviewsAll = useRoleAccess(CompanyAccess.ReviewListView, { scope: PermissionScope.All });
    const canViewReviewsSelf = useRoleAccess(CompanyAccess.ReviewListView, { scope: PermissionScope.Self });

    const canViewEmployeeServicesAll = useRoleAccess(CompanyAccess.EmployeeServicesView, {
        scope: PermissionScope.All,
    });
    const canViewEmployeeServicesSelf = useRoleAccess(CompanyAccess.EmployeeServicesView, {
        scope: PermissionScope.Self,
    });

    const canViewScheduleAll = useRoleAccess(CompanyAccess.ScheduleCalendarView, { scope: PermissionScope.All });
    const canViewScheduleSelf = useRoleAccess(CompanyAccess.ScheduleCalendarView, { scope: PermissionScope.Self });

    const canViewBookingSettingsAll = useRoleAccess(CompanyAccess.EmployeeBookingSettingsView, {
        scope: PermissionScope.All,
    });
    const canViewBookingSettingsSelf = useRoleAccess(CompanyAccess.EmployeeBookingSettingsView, {
        scope: PermissionScope.Self,
    });
    const canEditBookingSettingsAll = useRoleAccess(CompanyAccess.EmployeeBookingSettingsEdit, {
        scope: PermissionScope.All,
    });
    const canEditBookingSettingsSelf = useRoleAccess(CompanyAccess.EmployeeBookingSettingsEdit, {
        scope: PermissionScope.Self,
    });

    const canDismissEmployeeAll = useRoleAccess(CompanyAccess.EmployeeDismiss, { scope: PermissionScope.All });
    const canDismissEmployeeSelf = useRoleAccess(CompanyAccess.EmployeeDismiss, { scope: PermissionScope.Self });

    const canAccessEmployeeServicesModule = useModulesAccess([
        AllModulesTagName.SERVICES,
        AllModulesTagName.SERVICE_PROVIDES,
    ]);

    const isRoleAccessPending =
        !isAccessContextReady || !accessService.isRoleAccessReady() || accessService.isRoleAccessLoading();
    const isPremiumTariff = accessService.isPremium;
    const shouldShowOnlySelf = !isRoleAccessPending && !canViewEmployeeListAll;
    const hasHeaderActions =
        canImportEmployees || canExportEmployees || canCreateEmployee || canAddEmployeeFromAnotherBranch;
    const showBranchFilter = !isRoleAccessPending && canViewEmployeeListAll;

    return useMemo(
        () => ({
            isRoleAccessPending,
            isPremiumTariff,
            header: {
                canImportEmployees,
                canExportEmployees,
                canCreateEmployee,
                canAddEmployeeFromAnotherBranch,
                hasHeaderActions,
            },
            list: {
                canViewEmployeeListAll,
                canViewEmployeeListSelf,
                shouldShowOnlySelf,
                showBranchFilter,
            },
            accessSwitches: {
                canView: canViewAccessSwitches,
                canManage: canManageAccessSwitches,
            },
            employeeEntryAccess: {
                canViewAll: canViewEmployeeEntryAll,
                canViewSelf: canViewEmployeeEntrySelf,
                canEditAll: canEditEmployeeEntryAll,
                canEditSelf: canEditEmployeeEntrySelf,
            },
            salaryAccess: {
                canViewAll: canViewSalaryAll,
                canViewSelf: canViewSalarySelf,
                canEditAll: canEditSalaryAll,
                canEditSelf: canEditSalarySelf,
            },
            reviewAccess: {
                canViewAll: canViewReviewsAll,
                canViewSelf: canViewReviewsSelf,
            },
            servicesAccess: {
                canViewAll: canViewEmployeeServicesAll,
                canViewSelf: canViewEmployeeServicesSelf,
            },
            scheduleAccess: {
                canViewAll: canViewScheduleAll,
                canViewSelf: canViewScheduleSelf,
            },
            bookingAccess: {
                canViewAll: canViewBookingSettingsAll,
                canViewSelf: canViewBookingSettingsSelf,
                canEditAll: canEditBookingSettingsAll,
                canEditSelf: canEditBookingSettingsSelf,
            },
            dismissAccess: {
                canAll: canDismissEmployeeAll,
                canSelf: canDismissEmployeeSelf,
            },
            postCreate: {
                canOpenEmployeeServicesSetup:
                    Boolean(newEmployeeId) && canAccessEmployeeServicesModule && canViewEmployeeServicesAll,
            },
        }),
        [
            canAddEmployeeFromAnotherBranch,
            canCreateEmployee,
            canDismissEmployeeAll,
            canDismissEmployeeSelf,
            canEditBookingSettingsAll,
            canEditBookingSettingsSelf,
            canEditEmployeeEntryAll,
            canEditEmployeeEntrySelf,
            canEditSalaryAll,
            canEditSalarySelf,
            canExportEmployees,
            canImportEmployees,
            canManageAccessSwitches,
            canViewAccessSwitches,
            canViewBookingSettingsAll,
            canViewBookingSettingsSelf,
            canViewEmployeeEntryAll,
            canViewEmployeeEntrySelf,
            canViewEmployeeListAll,
            canViewEmployeeListSelf,
            canViewEmployeeServicesAll,
            canViewEmployeeServicesSelf,
            canViewReviewsAll,
            canViewReviewsSelf,
            canViewSalaryAll,
            canViewSalarySelf,
            canViewScheduleAll,
            canViewScheduleSelf,
            hasHeaderActions,
            isPremiumTariff,
            isRoleAccessPending,
            newEmployeeId,
            shouldShowOnlySelf,
            showBranchFilter,
            canAccessEmployeeServicesModule,
        ]
    );
};
